import { cva } from "class-variance-authority";
import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../cn";

/**
 * Which kind of nothing this is.
 *
 * Required, and that is the whole design of the component. The three cases the
 * copy already distinguishes with real care were drawn identically — the same
 * dashed box, the same heading weight, the same position, differing only in
 * whether one of them had a button — so the state a reader can act on and the
 * state they cannot looked the same until both had been read. On a fresh
 * project the empty state *is* the product, which makes that the largest single
 * design failure in the console.
 *
 * Squint at all three: a centred invitation with a coloured button, a
 * warning-tinted band, a grey line inside a table. No reading required.
 */
export type EmptyStateKind = "nothing-yet" | "blocked" | "filtered";

const empty = cva("", {
  variants: {
    kind: {
      /*
        Nothing yet — *do this next*. The reader has arrived somewhere real and
        there is work to start. Centred in the list's own surface, and the only
        one of the three that gets accent anywhere or a filled button.
      */
      "nothing-yet": [
        "flex flex-col items-center gap-3 rounded-[var(--radius-card)] border border-rule",
        "bg-surface px-6 py-14 text-center",
      ],
      /*
        Blocked on a prerequisite — *do something else first*. Left-aligned,
        because it is an explanation rather than an invitation, and a centred
        hero invites. The ground and the stripe are what make it legible as a
        held door before a word of it is read.
      */
      blocked: [
        "flex items-start gap-4 rounded-[var(--radius-card)] border-l-3 border-l-warning",
        "bg-warning-soft px-6 py-5 text-left",
      ],
      /*
        The filter matched nothing — *widen it*. It lives **inside** the list,
        at row height, on the sunk ground, with the chips that caused it still
        visible directly above. Promoting it to a centred hero hides the control
        that excluded the data, which is the one thing the reader needs.
      */
      filtered: "flex min-h-11 items-center gap-3 bg-sunk px-4 py-3 text-left",
    },
  },
});

export interface EmptyStateProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  /** Which of the three. Required: a caller who has not decided has not designed. */
  kind: EmptyStateKind;
  /** What is true. Not "no data" — say what is missing and why that matters. */
  title: ReactNode;
  /** One sentence: what the thing is for, or what has to happen first. */
  description?: ReactNode;
  /**
   * The way on.
   *
   * `nothing-yet` starts the thing and takes a `primary`. `blocked` points at
   * the prerequisite screen and takes a `secondary` — never at this screen,
   * which is the one the reader cannot act on. `filtered` takes a `ghost` that
   * clears the filter.
   */
  action?: ReactNode;
  /**
   * The glyph in the accent-tinted frame, on `nothing-yet` only.
   *
   * A 16px stroke mark. Omitted, the frame is still drawn: the round accent
   * shape at the top of a centred column is half of what makes this case
   * recognisable across a room.
   */
  glyph?: ReactNode;
  /**
   * `filtered` only: draw it as a centred card with a quiet glyph, for a screen whose list fills
   * the page and has no row to sit inside. The kind, and so the meaning, stays `filtered`.
   */
  centered?: boolean;
}

/**
 * Nothing here — and which kind of nothing, drawn so it can be told apart.
 *
 * An error is a fourth thing and is not in here: see `ErrorState`. A reader
 * must never be told to create something when the truth is that the read
 * failed.
 *
 * `nothing-yet` draws its own surface, which is right on a screen whose list is
 * empty and wrong inside a panel that has already drawn one — a card inside a
 * card is the failure the containment rule exists to stop. A panel passes
 * `className="border-0 bg-transparent"` and keeps the centring, the glyph and
 * the sentence.
 */
export function EmptyState({
  className,
  kind,
  title,
  description,
  action,
  glyph,
  centered = false,
  ...props
}: EmptyStateProps) {
  if (kind === "filtered" && !centered) {
    return (
      <div
        data-testid="empty"
        data-kind={kind}
        className={cn(empty({ kind }), className)}
        {...props}
      >
        <p className="min-w-0 text-prose text-ink-2">
          <span className="text-ink">{title}</span>
          {description !== undefined ? <> {description}</> : null}
        </p>
        {action ? <div className="ml-auto shrink-0">{action}</div> : null}
      </div>
    );
  }

  if (kind === "blocked") {
    return (
      <div
        data-testid="empty"
        data-kind={kind}
        className={cn(empty({ kind }), className)}
        {...props}
      >
        <WarningMark />
        <div className="min-w-0">
          <h2 className="text-prose font-semibold text-warning-text">{title}</h2>
          {description !== undefined ? (
            <p className="mt-2 max-w-prose text-prose text-ink">{description}</p>
          ) : null}
          {action ? <div className="mt-4">{action}</div> : null}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="empty"
      data-kind={kind}
      className={cn(empty({ kind: centered ? "nothing-yet" : kind }), className)}
      {...props}
    >
      <span
        className={cn(
          "flex size-10 items-center justify-center rounded-full",
          centered ? "bg-surface-2 text-ink-3" : "bg-accent-soft text-accent-text",
        )}
      >
        {glyph ?? <SparkMark />}
      </span>
      <h2 className="text-prose font-semibold text-ink">{title}</h2>
      {description !== undefined ? (
        <p className="max-w-md text-prose text-ink-2">{description}</p>
      ) : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

/** 16px stroke marks on a 24px grid, at 1.8, in `currentColor` — house rules §8. */
function WarningMark() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="mt-0.5 size-4 shrink-0 text-warning-text"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 4.5 21 19.5H3z" />
      <path d="M12 10v4M12 17v.5" />
    </svg>
  );
}

function SparkMark() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="size-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 3.5 13.9 9.1 19.5 11l-5.6 1.9L12 18.5l-1.9-5.6L4.5 11l5.6-1.9z" />
    </svg>
  );
}
